import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Radio, Play, Wifi, WifiOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/music`;
const NAMES = { piscina: "Piscina", spa: "Spa", ristorante: "Ristorante" };

// Zone receiver for an old Android phone: plays the zone's Icecast channel and
// follows regia commands (volume / pause / theme). Open once in a kiosk browser.
export default function RadioReceiverPage() {
  const { zone } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const key = params.get("key") || localStorage.getItem("radio_key") || "";
  const audioRef = useRef(null);
  const [cfg, setCfg] = useState(null);
  const [desired, setDesired] = useState({ effective_playing: true, volume: 60, effective_theme_id: null });
  const [online, setOnline] = useState(false);
  const [started, setStarted] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (params.get("key")) localStorage.setItem("radio_key", params.get("key")); }, [params]);

  const headers = { "X-Music-Key": key };
  const streamUrl = cfg?.stream_base ? `${cfg.stream_base}/${zone}.mp3` : "";

  useEffect(() => {
    const load = () => axios.get(`${API}/agent/config`, { headers }).then((r) => { setCfg(r.data); setOnline(true); }).catch((e) => { setOnline(false); setErr(e?.response?.status === 401 ? "Chiave non valida" : "Server non raggiungibile"); });
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zone, key]);

  useEffect(() => {
    if (!started) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/agent/${zone}/poll`, { headers });
        setDesired(data.desired);
        setOnline(true);
        const a = audioRef.current;
        if (a) {
          a.volume = Math.max(0, Math.min(1, (data.desired.volume ?? 60) / 100));
          if (data.desired.effective_playing && a.paused) a.play().catch(() => {});
          if (!data.desired.effective_playing && !a.paused) a.pause();
          if (data.commands.some((c) => c.cmd === "skip" || c.cmd === "reload")) { a.load(); if (data.desired.effective_playing) a.play().catch(() => {}); }
        }
        await axios.post(`${API}/agent/report`, { zone_id: zone, kind: "zone", now_playing: streamUrl ? "stream" : "", theme_id: data.desired.effective_theme_id, volume: data.desired.volume, playing: !!(a && !a.paused), error: a?.error ? "audio error" : null }, { headers });
      } catch (e) { setOnline(false); }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, zone, key, streamUrl]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const retry = () => setTimeout(() => { a.load(); if (desired.effective_playing) a.play().catch(() => {}); }, 4000);
    a.addEventListener("error", retry); a.addEventListener("stalled", retry); a.addEventListener("ended", retry);
    return () => { a.removeEventListener("error", retry); a.removeEventListener("stalled", retry); a.removeEventListener("ended", retry); };
  }, [desired.effective_playing, started]);

  const start = () => { setStarted(true); const a = audioRef.current; if (a) { a.volume = (desired.volume ?? 60) / 100; a.play().catch(() => {}); } };
  const theme = cfg?.themes?.find((t) => t.id === desired.effective_theme_id);
  const mixed = window.location.protocol === "https:" && /^http:/.test(cfg?.stream_base || "");

  if (!zone) {
    return (
      <div className="min-h-screen bg-[#1a0c0a] text-[#f7ebdf] flex flex-col items-center justify-center p-6 text-center select-none" data-testid="radio-zone-picker">
        <Radio className="h-10 w-10 text-[#C48B31] mb-3" />
        <div className="text-xs uppercase tracking-[0.3em] text-[#C48B31]">Radio Pozzo dei Giganti</div>
        <h1 className="font-serif text-3xl font-bold mt-1">Quale zona è questo telefono?</h1>
        <div className="grid gap-3 w-full max-w-sm mt-6">
          {Object.entries(NAMES).map(([id, name]) => (
            <button key={id} onClick={() => navigate(`/radio/${id}${key ? `?key=${encodeURIComponent(key)}` : ""}`)} className="h-20 rounded-3xl border-2 border-[#f7ebdf]/20 bg-white/5 text-2xl font-bold active:scale-95 transition-transform" data-testid={`radio-pick-${id}`}>{name}</button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a0c0a] text-[#f7ebdf] flex flex-col items-center justify-center p-6 text-center select-none" data-testid="radio-receiver" data-zone={zone} data-started={started}>
      <Radio className="h-10 w-10 text-[#C48B31] mb-3" />
      <div className="text-xs uppercase tracking-[0.3em] text-[#C48B31]">Radio Pozzo dei Giganti</div>
      <h1 className="font-serif text-4xl font-bold mt-1" data-testid="radio-receiver-zone">{NAMES[zone] || zone}</h1>
      <div className="mt-2 text-sm text-[#d9c3b2]" data-testid="radio-receiver-theme">{theme ? `Tema: ${theme.name}` : desired.effective_playing ? "In attesa del palinsesto" : "In pausa"} · volume {desired.volume ?? 60}%</div>
      <div className={`mt-3 inline-flex items-center gap-1 text-xs ${online ? "text-emerald-300" : "text-rose-300"}`} data-testid="radio-receiver-status">{online ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />} {online ? "collegato alla regia" : err || "non collegato"}</div>
      {streamUrl && <audio ref={audioRef} src={streamUrl} preload="none" data-testid="radio-receiver-audio" />}
      {!started && (
        <button onClick={start} disabled={!streamUrl} className="mt-8 h-16 px-10 rounded-full bg-[#C48B31] text-[#1a0c0a] font-bold text-lg inline-flex items-center gap-2 disabled:opacity-40" data-testid="radio-receiver-start">
          <Play className="h-6 w-6" /> Avvia ricevitore
        </button>
      )}
      {!streamUrl && cfg && <div className="mt-4 text-xs text-rose-300" data-testid="radio-receiver-nostream">Indirizzo stazione non impostato (Admin → Musica → Stazione)</div>}
      {mixed && (
        <div className="mt-4 max-w-sm rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-xs text-amber-200" data-testid="radio-receiver-mixed-hint">
          Su questo telefono apri invece <span className="font-mono font-bold break-all">{cfg.stream_base}/radio.html</span>: la musica del notebook viaggia in http e Chrome non la riproduce da una pagina https.
        </div>
      )}
      {started && <div className="mt-6 text-[11px] text-[#9a8070]">Lascia questa pagina aperta. Il telefono segue da solo palinsesto e comandi.</div>}
    </div>
  );
}
